const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

/**
 * Release-safety controller for the custom Sinternet Cult header.
 *
 * The section's legacy inline script only closes through the logo and adds
 * duplicate listeners during theme-editor reloads. This controller uses
 * delegated capture handlers so every close path releases the page scroll
 * lock without rewriting the section's large Liquid file.
 */
(() => {
  const controllers = new Map();
  const LOGO_PREFIX = 'header-logo-click-';
  const ROOT_OPEN_CLASS = 'sinternet-menu-open';
  const ENHANCED_TOGGLE_ATTR = 'data-sinternet-submenu-toggle';
  const ENHANCED_SUBMENU_CLOSE_ATTR = 'data-sinternet-submenu-close';
  let resizeTimer;

  const getSectionId = (element) => {
    const logoClass = Array.from(element?.classList || []).find((name) => name.startsWith(LOGO_PREFIX));
    return logoClass ? logoClass.slice(LOGO_PREFIX.length) : null;
  };

  const getControllerForElement = (element) => {
    const sectionRoot = element?.closest?.('.shopify-section');
    if (!sectionRoot) return null;

    const logo = sectionRoot.querySelector(`[class*="${LOGO_PREFIX}"]`);
    const sectionId = getSectionId(logo);
    return sectionId ? controllers.get(sectionId) || createController(logo) : null;
  };

  const createController = (logo) => {
    const sectionId = getSectionId(logo);
    if (!sectionId) return null;

    controllers.get(sectionId)?.destroy();

    const sectionRoot = logo.closest('.shopify-section');
    const section = sectionRoot?.querySelector(`.section-${sectionId}`);
    const settings = sectionRoot?.querySelector(`.section-${sectionId}-settings`);
    if (!sectionRoot || !section || !settings) return null;

    let lastFocusedElement = null;
    let activeSubmenuToggle = null;

    const allItems = () => Array.from(sectionRoot.querySelectorAll(`.header-item-${sectionId}`));
    const submenuToggles = () => Array.from(sectionRoot.querySelectorAll(`.header-toggle-${sectionId}`));
    const submenuCloseControls = () =>
      Array.from(sectionRoot.querySelectorAll(`.header-submenu-close-${sectionId}`));
    const isOpen = () => section.classList.contains('active');

    const setClosedTransform = () => {
      section.style.transform = `translateY(-${settings.offsetHeight}px)`;
    };

    const prepareSubmenuToggles = () => {
      submenuToggles().forEach((toggleButton) => {
        if (toggleButton.tagName === 'BUTTON') return;
        toggleButton.setAttribute(ENHANCED_TOGGLE_ATTR, 'true');
        toggleButton.setAttribute('role', 'button');
        toggleButton.setAttribute('tabindex', '0');
        toggleButton.setAttribute('aria-haspopup', 'true');
        toggleButton.setAttribute('aria-expanded', 'false');
      });
    };

    const prepareSubmenuCloseControls = () => {
      submenuCloseControls().forEach((closeControl) => {
        if (closeControl.tagName === 'BUTTON') return;
        closeControl.setAttribute(ENHANCED_SUBMENU_CLOSE_ATTR, 'true');
        closeControl.setAttribute('role', 'button');
        closeControl.setAttribute('tabindex', '0');
        closeControl.setAttribute('aria-label', 'Back to main menu');
      });
    };

    const resetSubmenus = ({ restoreFocus = false } = {}) => {
      const toggleToRestore = activeSubmenuToggle;

      allItems().forEach((item) => {
        item.style.display = '';
        item.classList.remove('hide');
        item.querySelector(`.header-submenu-list-${sectionId}`)?.classList.remove('active');
        const toggle = item.querySelector(`.header-toggle-${sectionId}`);
        if (toggle) {
          toggle.style.display = '';
          toggle.setAttribute('aria-expanded', 'false');
        }
      });

      activeSubmenuToggle = null;
      if (restoreFocus && toggleToRestore?.isConnected) {
        toggleToRestore.focus({ preventScroll: true });
      }
    };

    const sync = (open) => {
      section.classList.toggle('active', open);
      logo.classList.toggle('active', open);
      sectionRoot.classList.toggle(`sinternet-menu-layer-active-${sectionId}`, open);
      document.documentElement.classList.toggle(ROOT_OPEN_CLASS, open);
      document.body.classList.toggle(ROOT_OPEN_CLASS, open);
      logo.setAttribute('aria-expanded', String(open));
      if (!open) setClosedTransform();
    };

    const open = () => {
      if (isOpen()) return;
      lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      sync(true);
    };

    const close = ({ restoreFocus = true } = {}) => {
      const ownsGlobalLock = isOpen() || sectionRoot.classList.contains(`sinternet-menu-layer-active-${sectionId}`);
      if (ownsGlobalLock) {
        sync(false);
        resetSubmenus();
      }

      if (restoreFocus && lastFocusedElement?.isConnected) {
        lastFocusedElement.focus({ preventScroll: true });
      }
      lastFocusedElement = null;
    };

    const toggle = () => {
      if (isOpen()) close();
      else open();
    };

    const openSubmenu = (toggleButton) => {
      const parentItem = toggleButton.closest('li');
      if (!parentItem) return;

      const submenu = parentItem.querySelector(`.header-submenu-list-${sectionId}`);
      if (!submenu) return;

      activeSubmenuToggle = toggleButton;
      allItems().forEach((item) => {
        if (item !== parentItem) item.style.display = 'none';
      });
      toggleButton.style.display = 'none';
      toggleButton.setAttribute('aria-expanded', 'true');
      parentItem.classList.add('hide');
      submenu.classList.add('active');

      const nextFocus =
        submenu.querySelector(`.header-submenu-close-${sectionId}`) ||
        submenu.querySelector('a[href], button, [tabindex]:not([tabindex="-1"])');
      nextFocus?.focus({ preventScroll: true });
    };

    logo.setAttribute('role', 'button');
    logo.setAttribute('tabindex', '0');
    logo.setAttribute('aria-expanded', String(isOpen()));
    logo.setAttribute('aria-label', 'Toggle navigation menu');
    prepareSubmenuToggles();
    prepareSubmenuCloseControls();

    if (!isOpen()) setClosedTransform();

    const controller = {
      sectionId,
      sectionRoot,
      logo,
      isOpen,
      open,
      close,
      toggle,
      openSubmenu,
      resetSubmenus,
      refresh() {
        if (window.innerWidth >= 1024) close({ restoreFocus: false });
        else if (!isOpen()) setClosedTransform();
      },
      destroy() {
        close({ restoreFocus: false });
        logo.removeAttribute('role');
        logo.removeAttribute('tabindex');
        logo.removeAttribute('aria-expanded');
        logo.removeAttribute('aria-label');
        sectionRoot.querySelectorAll(`[${ENHANCED_TOGGLE_ATTR}]`).forEach((toggleButton) => {
          toggleButton.removeAttribute(ENHANCED_TOGGLE_ATTR);
          toggleButton.removeAttribute('role');
          toggleButton.removeAttribute('tabindex');
          toggleButton.removeAttribute('aria-haspopup');
          toggleButton.removeAttribute('aria-expanded');
        });
        sectionRoot.querySelectorAll(`[${ENHANCED_SUBMENU_CLOSE_ATTR}]`).forEach((closeControl) => {
          closeControl.removeAttribute(ENHANCED_SUBMENU_CLOSE_ATTR);
          closeControl.removeAttribute('role');
          closeControl.removeAttribute('tabindex');
          closeControl.removeAttribute('aria-label');
        });
        controllers.delete(sectionId);
      },
    };

    controllers.set(sectionId, controller);
    return controller;
  };

  const initializeHeaders = (scope = document) => {
    scope.querySelectorAll(`[class*="${LOGO_PREFIX}"]`).forEach((logo) => {
      if (getSectionId(logo)) createController(logo);
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      const logo = event.target.closest?.(`[class*="${LOGO_PREFIX}"]`);
      if (logo) {
        const controller = getControllerForElement(logo);
        if (!controller) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        controller.toggle();
        return;
      }

      const controller = getControllerForElement(event.target);
      const toggleButton = controller
        ? event.target.closest?.(`.header-toggle-${controller.sectionId}`)
        : null;
      if (toggleButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        controller.openSubmenu(toggleButton);
        return;
      }

      const submenuClose = event.target.closest?.('[class*="header-submenu-close-"]');
      if (submenuClose) {
        const submenuController = getControllerForElement(submenuClose);
        if (!submenuController) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        submenuController.resetSubmenus({ restoreFocus: true });
        return;
      }

      const link = event.target.closest?.('.shopify-section a[href]');
      if (link) {
        const linkController = getControllerForElement(link);
        if (linkController?.isOpen()) linkController.close({ restoreFocus: false });
      }
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      const logo = event.target.closest?.(`[class*="${LOGO_PREFIX}"]`);
      if (logo && (event.key === 'Enter' || event.key === ' ')) {
        const controller = getControllerForElement(logo);
        if (!controller) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        controller.toggle();
        return;
      }

      const controller = getControllerForElement(event.target);
      const toggleButton = controller
        ? event.target.closest?.(`.header-toggle-${controller.sectionId}`)
        : null;
      if (
        toggleButton &&
        toggleButton.tagName !== 'BUTTON' &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        controller.openSubmenu(toggleButton);
        return;
      }

      const submenuClose = controller
        ? event.target.closest?.(`.header-submenu-close-${controller.sectionId}`)
        : null;
      if (
        submenuClose &&
        submenuClose.tagName !== 'BUTTON' &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        controller.resetSubmenus({ restoreFocus: true });
        return;
      }

      if (event.key === 'Escape') {
        const openController = Array.from(controllers.values()).find((currentController) => currentController.isOpen());
        if (openController) {
          event.preventDefault();
          event.stopImmediatePropagation();
          openController.close();
        }
      }
    },
    true
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      controllers.forEach((controller) => {
        if (controller.isOpen() && !controller.sectionRoot.contains(event.target)) controller.close();
      });
    },
    true
  );

  const closeForNavigation = () => {
    controllers.forEach((controller) => controller.close({ restoreFocus: false }));
  };

  const refreshForViewport = () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      controllers.forEach((controller) => controller.refresh());
    }, 150);
  };

  window.addEventListener('resize', refreshForViewport);
  window.addEventListener('orientationchange', refreshForViewport);
  window.addEventListener('popstate', closeForNavigation);
  window.addEventListener('pagehide', closeForNavigation);

  document.addEventListener('shopify:section:load', (event) => initializeHeaders(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail?.sectionId;
    if (sectionId) controllers.get(sectionId)?.destroy();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeHeaders(), { once: true });
  } else {
    initializeHeaders();
  }
})();
